import React, { useState } from 'react';
import { Loader2, ShieldCheck, Send, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { bookingsApi } from '../../services/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const STATUS_COPY = {
  requested: { label: 'Requested', cls: 'bg-amber-100 text-amber-800' },
  released: { label: 'Telex released', cls: 'bg-success-50 text-success-700' },
  rejected: { label: 'Declined', cls: 'bg-error-50 text-error-700' },
};

// Surrender-of-originals workflow: shipper requests, Bridge approves; consignee then collects without paper B/L.
export function TelexReleaseCard({ booking, staff, onChanged }) {
  const tlx = booking.telex_release || {};
  const si = booking.shipping_instructions || {};
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const [override, setOverride] = useState(false);
  const status = tlx.status;
  const blIssued = Boolean(booking.bl_issued_at);
  const waybill = si.number_of_originals === 0;

  async function run(action, fn) {
    setBusy(action);
    try {
      const res = await fn();
      toast.success(res.data.message, { description: res.data.email && res.data.email.status !== 'sent' ? 'Confirmation PDF ready — ' + res.data.email.reason : (res.data.email ? 'Confirmation emailed to ' + res.data.email.to.join(', ') : undefined) });
      setNote(''); setOverride(false);
      if (onChanged) onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setBusy('');
    }
  }

  if (booking.status === 'cancelled') return null;
  return (
    <div className="border border-gray-200 rounded-sm p-4 mb-4" data-testid="telex-release-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-sm bg-gray-100 flex items-center justify-center text-ink"><ShieldCheck className="h-4 w-4" /></div>
        <div className="flex-1 min-w-[220px]">
          <p className="font-medium text-ink">Telex release <span className="text-gray-500 font-normal">— surrender of original B/L</span></p>
          <p className="text-sm text-gray-600">
            {waybill ? 'Sea waybill shipment — no originals to surrender.' : !blIssued ? 'Available once the bill of lading has been issued.' : status === 'released' ? 'Originals surrendered at origin on ' + formatDate(tlx.released_at) + ' · confirmation ' + tlx.confirmation_number + '. Cargo releases to the consignee against identity.' : status === 'requested' ? 'Requested ' + formatDate(tlx.requested_at) + ' by ' + tlx.requested_by + (tlx.note ? ' — “' + tlx.note + '”' : '') : status === 'rejected' ? 'Declined: ' + (tlx.note || '') : 'Ask Südvik to release the cargo at destination without presentation of the paper originals.'}
          </p>
        </div>
        {status && STATUS_COPY[status] && <span className={"text-xs font-medium px-2.5 py-1 rounded-full " + STATUS_COPY[status].cls} data-testid="telex-status">{STATUS_COPY[status].label}</span>}
        {!waybill && blIssued && status !== 'released' && !staff && status !== 'requested' && (
          <Button size="sm" className="rounded-sm bg-ink hover:bg-ink-800" disabled={busy !== ''} onClick={function () { run('request', function () { return bookingsApi.telexRequest(booking.id, { note: note || null }); }); }} data-testid="telex-request-btn">
            {busy === 'request' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Request telex release</>}
          </Button>
        )}
      </div>
      {!waybill && blIssued && staff && status !== 'released' && (
        <div className="mt-4 pt-4 border-t border-gray-200 grid md:grid-cols-[1fr_auto] gap-3 items-start" data-testid="telex-staff-actions">
          <div>
            <Textarea rows={2} value={note} onChange={function (e) { setNote(e.target.value); }} placeholder={status === 'requested' ? 'Note to the shipper (required to decline, required with an override)' : 'Optional note'} className="rounded-sm" data-testid="telex-note" />
            {si.freight_terms === 'COLLECT' && booking.payment_status !== 'paid' && (
              <label className="flex items-center gap-2 text-sm text-amber-800 mt-2"><input type="checkbox" checked={override} onChange={function (e) { setOverride(e.target.checked); }} data-testid="telex-override" /> Freight is collect and unpaid — override (admin only, note required)</label>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="rounded-sm bg-ink hover:bg-ink-800" disabled={busy !== ''} onClick={function () { run('approve', function () { return bookingsApi.telexApprove(booking.id, { note: note || null, override: override }); }); }} data-testid="telex-approve-btn">
              {busy === 'approve' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1.5" /> {status === 'requested' ? 'Approve' : 'Grant telex release'}</>}
            </Button>
            {status === 'requested' && (
              <Button size="sm" variant="outline" className="rounded-sm" disabled={busy !== ''} onClick={function () { run('reject', function () { return bookingsApi.telexReject(booking.id, { note: note }); }); }} data-testid="telex-reject-btn">
                {busy === 'reject' ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="h-4 w-4 mr-1.5" /> Decline</>}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
